import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { QRScanner } from '../components/QRScanner';
import { parseQRCodeData } from '../services/qrCodeService';
import { getFloc } from '../services/flocService';
import { QrCodeIcon, CubeIcon, MapPinIcon, CameraIcon } from '@heroicons/react/24/outline';

export function QuickStartPage() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [showScanner, setShowScanner] = useState(false);
  const [scanMode, setScanMode] = useState<'normal' | 'nameplate'>('normal');

  const handleScan = async (qrData: string) => {
    // Check if it's a FLOC QR code with prefix
    if (qrData.startsWith('FLOC:')) {
      const flocId = qrData.substring(5); // Remove "FLOC:" prefix
      if (currentOrg) {
        try {
          const floc = await getFloc(currentOrg.id, flocId);
          if (floc) {
            // If in nameplate mode, navigate to nameplate capture page
            if (scanMode === 'nameplate') {
              navigate(`/flocs/${flocId}/capture-nameplate`);
            } else {
              navigate(`/flocs/${flocId}`);
            }
            setShowScanner(false);
            return;
          }
        } catch (error) {
          // Not a valid FLOC ID
        }
      }
    }

    // Try parsing as JSON (legacy format for assets/sites)
    const parsed = parseQRCodeData(qrData);

    if (parsed) {
      // Legacy QR code format - verify organization matches
      if (parsed.orgId !== currentOrg?.id) {
        alert('This QR code belongs to a different organization.');
        setShowScanner(false);
        return;
      }

      // Navigate based on type
      if (parsed.type === 'asset') {
        navigate(`/assets/${parsed.id}/inspections`);
      } else if (parsed.type === 'floc') {
        navigate(`/flocs/${parsed.id}`);
      } else if (parsed.type === 'site') {
        navigate(`/sites/${parsed.id}`);
      }
      setShowScanner(false);
      return;
    }

    // Try as plain FLOC ID (backward compatibility for QR codes without prefix)
    if (currentOrg) {
      try {
        const floc = await getFloc(currentOrg.id, qrData);
        if (floc) {
          navigate(`/flocs/${qrData}`);
          setShowScanner(false);
          return;
        }
      } catch (error) {
        // Not a valid FLOC ID
      }
    }

    // Invalid QR code
    alert('Invalid QR code. Please scan a valid asset or FLOC QR code.');
    setShowScanner(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Quick Start</h1>
        <p className="text-gray-600 mt-1">
          Scan a QR code to quickly start an inspection or view details
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Scan QR Code */}
        <div className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => { setScanMode('normal'); setShowScanner(true); }}>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
              <QrCodeIcon className="w-8 h-8 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Scan QR Code
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Scan an asset or FLOC QR code to quickly access details
            </p>
            <button className="btn btn-primary">
              Open Scanner
            </button>
          </div>
        </div>

        {/* Capture Nameplate */}
        <div className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => { setScanMode('nameplate'); setShowScanner(true); }}>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
              <CameraIcon className="w-8 h-8 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Capture Nameplate
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Scan FLOC QR, select asset, and capture nameplate info
            </p>
            <button className="btn btn-secondary">
              Start Workflow
            </button>
          </div>
        </div>

        {/* Browse Assets */}
        <div className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/assets')}>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <CubeIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Browse Assets
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              View all assets and manually select one to inspect
            </p>
            <button className="btn btn-secondary">
              View Assets
            </button>
          </div>
        </div>

        {/* Browse FLOCs */}
        <div className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/flocs')}>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <MapPinIcon className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Browse FLOCs
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              View all functional locations and their assets
            </p>
            <button className="btn btn-secondary">
              View FLOCs
            </button>
          </div>
        </div>

        {/* View Inspections */}
        <div className="card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/inspections')}>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
              <QrCodeIcon className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              View Inspections
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              See all scheduled and completed inspections
            </p>
            <button className="btn btn-secondary">
              View Inspections
            </button>
          </div>
        </div>
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Instructions */}
      <div className="card mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          How to Use QR Codes
        </h2>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-xs mr-3 flex-shrink-0 mt-0.5">
              1
            </span>
            <p>
              <strong className="text-gray-900">Generate QR Codes:</strong> Go to an asset or FLOC detail page and click "QR Code" to generate and print labels.
            </p>
          </div>
          <div className="flex items-start">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-xs mr-3 flex-shrink-0 mt-0.5">
              2
            </span>
            <p>
              <strong className="text-gray-900">Attach Labels:</strong> Print and attach QR code labels to physical assets or post them at FLOC locations.
            </p>
          </div>
          <div className="flex items-start">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-600 font-semibold text-xs mr-3 flex-shrink-0 mt-0.5">
              3
            </span>
            <p>
              <strong className="text-gray-900">Scan to Inspect:</strong> Use the scanner above to quickly start an inspection by scanning the QR code on the asset.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
